'use client'

import { Shield } from 'lucide-react'
import { LenisFadeIn } from '@/components/animations/LenisReveal'
import Hero from '@/components/Hero'
import Link from 'next/link'
import { PoweredByPaddle } from '@/components/PoweredByPaddle'

export default function RefundPolicyPage() {
  return (
    <div className='container mx-auto px-4 py-16 animate-fade-in'>
      <Hero
        title={<>Refund Policy</>}
        description='Radar by Pixelmojo. Effective April 2026'
        eyebrow='Legal'
        ctaText='Contact Us'
        ctaHref='/contact-us'
      />

      {/* Policy Content */}
      <LenisFadeIn delay={200}>
        <section className='max-w-3xl mx-auto mb-32'>
          <div className='border-2 border-border bg-card/70 p-8 md:p-12'>
            <p className='text-lg text-foreground leading-relaxed mb-6'>
              You may request a refund within <strong>14 days</strong> of the
              transaction date. Requests are reviewed and processed through
              Paddle where eligible.
            </p>
            <p className='text-foreground/80 leading-relaxed mb-6'>
              To request a refund, email{' '}
              <a
                href='mailto:founders@pixelmojo.io'
                className='text-primary hover:underline font-semibold'
              >
                founders@pixelmojo.io
              </a>{' '}
              with your order ID.
            </p>
            <p className='text-foreground/80 leading-relaxed'>
              All payments are processed by{' '}
              <a
                href='https://paddle.com'
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary hover:underline font-semibold'
              >
                Paddle.com
              </a>{' '}
              as Merchant of Record. Refund requests are subject to{' '}
              <a
                href='https://www.paddle.com/legal/invoiced-consumer-terms'
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary hover:underline'
              >
                Paddle&apos;s refund policy
              </a>
              .
            </p>
            <div className='mt-6 flex justify-start'>
              <PoweredByPaddle variant='card' />
            </div>
          </div>
        </section>
      </LenisFadeIn>

      {/* Contact */}
      <LenisFadeIn delay={300}>
        <section className='max-w-3xl mx-auto mb-32'>
          <div className='border-2 border-primary p-8 bg-primary/5'>
            <div className='flex items-start gap-6'>
              <div className='flex h-14 w-14 items-center justify-center bg-primary/20 flex-shrink-0'>
                <Shield className='h-7 w-7 text-primary' />
              </div>
              <div>
                <h2 className='text-2xl font-bold mb-3 text-foreground'>
                  Need Help?
                </h2>
                <p className='text-muted-foreground mb-4'>
                  For refund requests or billing questions, reach out to us
                  directly.
                </p>
                <div className='grid md:grid-cols-2 gap-4'>
                  <div className='p-4 bg-background/50 border border-border'>
                    <p className='text-sm font-semibold text-muted-foreground mb-1'>
                      Pixelmojo
                    </p>
                    <a
                      href='mailto:founders@pixelmojo.io'
                      className='text-primary hover:underline font-bold'
                    >
                      founders@pixelmojo.io
                    </a>
                  </div>
                  <div className='p-4 bg-background/50 border border-border'>
                    <p className='text-sm font-semibold text-muted-foreground mb-1'>
                      Paddle (Payment Processor)
                    </p>
                    <a
                      href='https://www.paddle.com/support'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-primary hover:underline font-bold'
                    >
                      paddle.com/support
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </LenisFadeIn>

      {/* Related Links */}
      <LenisFadeIn delay={400}>
        <section className='max-w-4xl mx-auto'>
          <div className='bg-muted/30 border border-border p-8 text-center'>
            <p className='text-sm text-muted-foreground mb-4'>
              Related policies
            </p>
            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <Link
                href='/terms-of-service'
                className='inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold transition-all'
              >
                Terms of Service →
              </Link>
              <Link
                href='/privacy-policy'
                className='inline-flex items-center gap-2 px-6 py-3 bg-muted hover:bg-muted/80 border border-border text-foreground font-semibold transition-all'
              >
                Privacy Policy →
              </Link>
            </div>
          </div>
        </section>
      </LenisFadeIn>
    </div>
  )
}
